import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ListIcon, LayoutGridIcon, PhoneIcon, CheckCircleIcon, AlertCircleIcon, ClockIcon, UserIcon, MapPinIcon, SearchIcon, FilterIcon } from 'lucide-react';
import { Card, CardHeader } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { Select } from '../components/ui/Select';
import { SearchableSelect } from '../components/ui/SearchableSelect';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { StatsCard } from '../components/ui/StatsCard';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { supabaseAdmin } from '../lib/supabase';
import { useRealtimeRefresh } from '../hooks/useRealtimeRefresh';
// ── TYPES ────────────────────────────────────────────────────────────────────
type VisitType = 'visita_venta' | 'visita_cobro' | 'reactivacion' | 'seguimiento';
type VisitStatus = 'pendiente' | 'visitado' | 'reprogramado' | 'codificacion';
type TeleventasResult = 'no_contesto' | 'promesa_pago' | 'venta_realizada' | 'seguimiento';
interface PlanEntry {
  id: string;
  fecha: Date;
  diaSemana: number;
  clienteNombre: string;
  codigoCliente: string;
  zona: string;
  vendedorId: string;
  vendedorNombre: string;
  tipoGestion: VisitType;
  estado: VisitStatus;
  hora?: string;
}
interface TeleventasEntry {
  id: string;
  clienteNombre: string;
  codigoCliente: string;
  fecha: Date;
  resultado: TeleventasResult;
  agente: string;
  notas?: string;
}
// ── HELPERS ──────────────────────────────────────────────────────────────────
const getMonday = (d: Date): Date => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.setDate(diff));
  monday.setHours(0, 0, 0, 0);
  return monday;
};
const getDayName = (d: Date): string => {
  const names = ['Domingo', 'Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  return names[d.getDay()];
};
const getVisitTypeColor = (type: VisitType) => {
  switch (type) {
    case 'visita_venta':
      return 'bg-blue-600 text-white border-blue-700';
    case 'visita_cobro':
      return 'bg-emerald-600 text-white border-emerald-700';
    case 'reactivacion':
      return 'bg-amber-600 text-white border-amber-700';
    case 'seguimiento':
      return 'bg-violet-600 text-white border-violet-700';
    default:
      return 'bg-slate-600 text-white';
  }
};
const getVisitTypeLabel = (type: VisitType) => {
  switch (type) {
    case 'visita_venta':
      return 'Visita de Venta';
    case 'visita_cobro':
      return 'Cobranza';
    case 'reactivacion':
      return 'Reactivación';
    case 'seguimiento':
      return 'Seguimiento';
    default:
      return type;
  }
};
const getStatusBadgeVariant = (status: VisitStatus) => {
  switch (status) {
    case 'visitado':
      return 'success';
    case 'reprogramado':
      return 'danger';
    case 'codificacion':
      return 'info';
    default:
      return 'warning';
  }
};
const getStatusLabel = (status: VisitStatus) => {
  switch (status) {
    case 'pendiente':
      return 'pendiente';
    case 'visitado':
      return 'completada';
    case 'reprogramado':
      return 'cancelada';
    case 'codificacion':
      return 'codificación';
    default:
      return status;
  }
};
const nextStatus = (current: VisitStatus): VisitStatus => {
  switch (current) {
    case 'pendiente':
      return 'visitado';
    case 'visitado':
      return 'reprogramado';
    case 'reprogramado':
      return 'pendiente';
    default:
      return 'pendiente';
  }
};
const getTeleventasResultBadge = (result: TeleventasResult) => {
  switch (result) {
    case 'venta_realizada':
      return <Badge variant="success" data-id="element-3992" data-mp="27zwxssfmiazq">Venta Realizada</Badge>;
    case 'promesa_pago':
      return <Badge variant="warning" data-id="element-3993" data-mp="2cy8bje91h5n8">Promesa Pago</Badge>;
    case 'no_contesto':
      return <Badge variant="danger" data-id="element-3994" data-mp="1yyqgwi92ay5y">No Contestó</Badge>;
    case 'seguimiento':
      return <Badge variant="info" data-id="element-3995" data-mp="22zsj97y9zqc5">Seguimiento</Badge>;
  }
};
// ── COMPONENT ────────────────────────────────────────────────────────────────
export function PlanificacionPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const {
    clients,
    users
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const isFieldVendor = !isAdmin && user?.permisos?.['new-visit'] === true;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<'calendar' | 'list'>('calendar');
  const [activeTab, setActiveTab] = useState<'planificacion' | 'televentas' | 'resumen'>('planificacion');
  const [planEntries, setPlanEntries] = useState<PlanEntry[]>([]);
  const [televentasEntries, setTeleventasEntries] = useState<TeleventasEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [isCallModalOpen, setIsCallModalOpen] = useState(false);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  // Form state — fecha stored as string "YYYY-MM-DD" to avoid timezone issues
  const [planFormDate, setPlanFormDate] = useState(new Date().toISOString().split('T')[0]);
  const [planFormCliente, setPlanFormCliente] = useState('');
  const [planFormCodigo, setPlanFormCodigo] = useState('');
  const [planFormZona, setPlanFormZona] = useState('');
  const [planFormTipo, setPlanFormTipo] = useState<VisitType>('visita_venta');
  const [planFormVendedorId, setPlanFormVendedorId] = useState('');
  const [planFormVendedorNombre, setPlanFormVendedorNombre] = useState('');
  const [callFormCliente, setCallFormCliente] = useState('');
  const [callFormCodigo, setCallFormCodigo] = useState('');
  const [callFormResultado, setCallFormResultado] = useState<TeleventasResult>('seguimiento');
  const [callFormNotas, setCallFormNotas] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 6;
  const [planSearchQuery, setPlanSearchQuery] = useState('');
  const PLAN_VISIBLE_LIMIT = 4;
  // ── SUPABASE LOAD ──────────────────────────────────────────────────────────
  const mapPlanRow = (row: any): PlanEntry => ({
    id: row.id,
    fecha: new Date(row.fecha),
    diaSemana: new Date(row.fecha).getDay(),
    clienteNombre: row.cliente_nombre,
    codigoCliente: row.codigo_cliente || '',
    zona: row.zona || '',
    vendedorId: row.vendedor_id || '',
    vendedorNombre: row.vendedor_nombre || '',
    tipoGestion: row.tipo_gestion as VisitType,
    estado: row.estado as VisitStatus,
    hora: new Date(row.fecha).toLocaleTimeString('es-DO', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    })
  });
  const loadPlanEntries = async () => {
    console.log('[PlanificacionPage] Loading plan entries...');
    try {
      // Limit to a reasonable number to avoid egress issues
      const {
        data,
        error
      } = await supabaseAdmin.from('plan_entries').select('*').order('fecha', {
        ascending: true
      }).limit(1000);
      if (error) {
        console.error('[PlanificacionPage] Error loading plan_entries:', error);
        return;
      }
      console.log(`[PlanificacionPage] Received ${data?.length || 0} plan entries`);
      if (data) setPlanEntries(data.map(mapPlanRow));
    } catch (err) {
      console.error('[PlanificacionPage] Error loading plan entries:', err);
    }
  };
  const loadTeleventasEntries = async () => {
    console.log('[PlanificacionPage] Loading televentas entries...');
    try {
      const {
        data,
        error
      } = await supabaseAdmin.from('televentas_entries').select('*').order('fecha', {
        ascending: false
      }).limit(100);
      if (error) {
        console.error('[PlanificacionPage] Error loading televentas:', error);
        return;
      }
      console.log(`[PlanificacionPage] Received ${data?.length || 0} televentas entries`);
      if (data) {
        setTeleventasEntries(data.map((row: any): TeleventasEntry => ({
          id: row.id,
          clienteNombre: row.cliente_nombre,
          codigoCliente: row.codigo_cliente || '',
          fecha: new Date(row.fecha),
          resultado: row.resultado as TeleventasResult,
          agente: row.agente,
          notas: row.notas || undefined
        })));
      }
    } catch (err) {
      console.error('[PlanificacionPage] Error loading televentas:', err);
    }
  };
  useEffect(() => {
    const init = async () => {
      setIsLoading(true);
      try {
        await Promise.all([loadPlanEntries(), loadTeleventasEntries()]);
      } catch (err) {
        console.error('Error initializing planning data:', err);
      } finally {
        // Ensure loading is ALWAYS disabled
        setIsLoading(false);
      }
    };
    init();
    // Safety timeout for this specific page
    const timer = setTimeout(() => setIsLoading(false), 5000);
    return () => clearTimeout(timer);
  }, []);
  const refreshPlanData = useCallback(() => {
    loadPlanEntries();
    loadTeleventasEntries();
  }, []);
  useRealtimeRefresh(['plan_entries'], refreshPlanData);
  // ── COMPUTED ───────────────────────────────────────────────────────────────
  // Field vendors only see their own plan entries
  const visiblePlanEntries = useMemo(() => {
    if (isAdmin || !isFieldVendor || !user) return planEntries;
    return planEntries.filter(e => e.vendedorId === user.id || e.vendedorNombre === user.nombre);
  }, [planEntries, isAdmin, isFieldVendor, user]);
  const startOfWeek = useMemo(() => getMonday(currentDate), [currentDate]);
  const weekDays = useMemo(() => {
    const days = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      days.push(d);
    }
    return days;
  }, [startOfWeek]);
  const weekRangeLabel = useMemo(() => {
    const end = new Date(startOfWeek);
    end.setDate(startOfWeek.getDate() + 4);
    return `Semana del ${startOfWeek.getDate()} al ${end.getDate()} de ${startOfWeek.toLocaleString('es-DO', {
      month: 'short'
    })}`;
  }, [startOfWeek]);
  const currentWeekEntries = useMemo(() => {
    const start = new Date(startOfWeek);
    start.setHours(0, 0, 0, 0);
    const end = new Date(startOfWeek);
    end.setDate(startOfWeek.getDate() + 5);
    end.setHours(0, 0, 0, 0);
    return visiblePlanEntries.filter(e => {
      const d = new Date(e.fecha);
      return d >= start && d < end;
    });
  }, [visiblePlanEntries, startOfWeek]);
  const stats = useMemo(() => ({
    programadas: currentWeekEntries.length,
    cobros: currentWeekEntries.filter(e => e.tipoGestion === 'visita_cobro' && e.estado === 'pendiente').length,
    reactivaciones: currentWeekEntries.filter(e => e.tipoGestion === 'reactivacion').length,
    disponibles: Math.max(0, clients.length - currentWeekEntries.length)
  }), [currentWeekEntries, clients]);
  // ── HANDLERS ───────────────────────────────────────────────────────────────
  const handlePrevWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() - 7);
    setCurrentDate(d);
  };
  const handleNextWeek = () => {
    const d = new Date(currentDate);
    d.setDate(d.getDate() + 7);
    setCurrentDate(d);
  };
  const resetPlanForm = () => {
    setPlanFormDate(new Date().toISOString().split('T')[0]);
    setPlanFormCliente('');
    setPlanFormCodigo('');
    setPlanFormZona('');
    setPlanFormTipo('visita_venta');
    // Auto-assign vendedor for field vendors
    if (isFieldVendor && user) {
      setPlanFormVendedorId(user.id);
      setPlanFormVendedorNombre(user.nombre);
    } else {
      setPlanFormVendedorId('');
      setPlanFormVendedorNombre('');
    }
    setSaveError('');
  };
  const resetCallForm = () => {
    setCallFormCliente('');
    setCallFormCodigo('');
    setCallFormResultado('seguimiento');
    setCallFormNotas('');
    setSaveError('');
  };
  const handleToggleStatus = async (entry: PlanEntry) => {
    const newStatus = nextStatus(entry.estado);
    setUpdatingStatusId(entry.id);
    try {
      const {
        error
      } = await supabaseAdmin.from('plan_entries').update({
        estado: newStatus
      }).eq('id', entry.id);
      if (!error) {
        setPlanEntries(prev => prev.map(e => e.id === entry.id ? {
          ...e,
          estado: newStatus
        } : e));
        addActivityLog('Cambió estado planificación', `Cliente: ${entry.clienteNombre} | ${entry.estado} → ${newStatus}`);
      }
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingStatusId(null);
    }
  };
  const handleSavePlan = async () => {
    if (!planFormCliente || !planFormDate) return;
    setSaveError('');
    setIsSaving(true);
    try {
      // Build date with noon time to avoid timezone edge cases
      const fecha = new Date(planFormDate + 'T10:00:00');
      const monday = getMonday(fecha);
      // Determine vendedor — use form selection or current user
      const vendedorId = planFormVendedorId || user?.id || null;
      const vendedorNombre = planFormVendedorNombre || user?.nombre || 'Usuario';
      const payload: Record<string, any> = {
        fecha: fecha.toISOString(),
        dia_semana: getDayName(fecha),
        cliente_nombre: planFormCliente,
        codigo_cliente: planFormCodigo || '',
        zona: planFormZona || 'General',
        vendedor_nombre: vendedorNombre,
        tipo_gestion: planFormTipo,
        estado: 'pendiente',
        semana_inicio: monday.toISOString().split('T')[0]
      };
      // Only include vendedor_id if it's a valid value (avoid FK constraint errors)
      if (vendedorId) {
        payload.vendedor_id = vendedorId;
      }
      const {
        data,
        error
      } = await supabaseAdmin.from('plan_entries').insert(payload).select();
      if (error) {
        console.error('Supabase error:', error);
        setSaveError(`Error al guardar: ${error.message}`);
        return;
      }
      // Success — add to local state immediately and close modal
      if (data && data.length > 0) {
        setPlanEntries(prev => [...prev, mapPlanRow(data[0])]);
      }
      addActivityLog('Creó planificación', `Cliente: ${planFormCliente} | Fecha: ${planFormDate} | Tipo: ${planFormTipo}`);
      setIsPlanModalOpen(false);
      resetPlanForm();
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveError('Error inesperado al guardar. Intente de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };
  const handleSaveCall = async () => {
    if (!callFormCliente) return;
    setSaveError('');
    setIsSaving(true);
    try {
      const payload: Record<string, any> = {
        cliente_nombre: callFormCliente,
        codigo_cliente: callFormCodigo || '',
        fecha: new Date().toISOString(),
        resultado: callFormResultado,
        agente: user?.nombre || 'Agente',
        notas: callFormNotas || ''
      };
      // Only include agente_id if valid
      if (user?.id) {
        payload.agente_id = user.id;
      }
      const {
        data,
        error
      } = await supabaseAdmin.from('televentas_entries').insert(payload).select();
      if (error) {
        console.error('Supabase error:', error);
        setSaveError(`Error al guardar: ${error.message}`);
        return;
      }
      if (data && data.length > 0) {
        const newEntry: TeleventasEntry = {
          id: data[0].id,
          clienteNombre: data[0].cliente_nombre,
          codigoCliente: data[0].codigo_cliente || '',
          fecha: new Date(data[0].fecha),
          resultado: data[0].resultado as TeleventasResult,
          agente: data[0].agente,
          notas: data[0].notas || undefined
        };
        setTeleventasEntries(prev => [newEntry, ...prev]);
      }
      addActivityLog('Registró llamada televentas', `Cliente: ${callFormCliente} | Resultado: ${callFormResultado}`);
      setIsCallModalOpen(false);
      resetCallForm();
    } catch (err: any) {
      console.error('Save error:', err);
      setSaveError('Error inesperado al guardar. Intente de nuevo.');
    } finally {
      setIsSaving(false);
    }
  };
  // ── RENDER ─────────────────────────────────────────────────────────────────
  return <div className="space-y-4 sm:space-y-6" data-id="element-3996" data-mp="3og41atv1vgxs">
      <PageHeader title="Planificación Comercial" subtitle="Gestión de rutas y televentas" icon={<CalendarIcon className="w-6 h-6" data-id="element-3998" data-mp="2vt17ug58dwee" />} data-id="element-3997" data-mp="30rupmkrjw9kd">
        
        <div className="flex items-center gap-2 sm:gap-3 w-full sm:w-auto flex-wrap" data-id="element-3999" data-mp="2vsi17vl16ufo">
          <Button variant="secondary" size="sm" onClick={refreshPlanData} isLoading={isLoading} leftIcon={<ClockIcon className="w-4 h-4" data-id="element-4001" data-mp="1g2qugwmhlth7" />} data-id="element-4000" data-mp="1ba7757xzzjz7">
            
            <span className="hidden sm:inline" data-id="element-4002" data-mp="12aartwxhzxgt">Recargar</span>
          </Button>
          <div className="flex items-center gap-1.5 sm:gap-2 bg-white border border-slate-200 rounded-xl p-1 shadow-sm" data-id="element-4003" data-mp="1287s2l6jmpyw">
            <button onClick={handlePrevWeek} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-4004" data-mp="1u1q8lacdipz4">
              
              <ChevronLeftIcon className="w-4 h-4 text-slate-500" data-id="element-4005" data-mp="1yzic71mds0j5" />
            </button>
            <span className="text-xs sm:text-sm font-medium text-slate-700 min-w-[120px] sm:min-w-[180px] text-center" data-id="element-4006" data-mp="1l1bp8giyzky9">
              {weekRangeLabel}
            </span>
            <button onClick={handleNextWeek} className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors" data-id="element-4007" data-mp="9lmyde2li1s6">
              
              <ChevronRightIcon className="w-4 h-4 text-slate-500" data-id="element-4008" data-mp="el9ebv9t5w4o" />
            </button>
          </div>
          <div className="bg-white border border-slate-200 p-1 rounded-xl flex items-center shadow-sm" data-id="element-4009" data-mp="15b1rem92hge6">
            <button onClick={() => setViewMode('calendar')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'calendar' ? 'bg-[#eef2fc] text-[#2952C4]' : 'text-slate-500 hover:text-slate-700'}`} title="Vista Calendario" data-id="element-4010" data-mp="11854wkgep04y">
              
              <LayoutGridIcon className="w-4 h-4" data-id="element-4011" data-mp="rd4qxerhspu8" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded-lg transition-all ${viewMode === 'list' ? 'bg-[#eef2fc] text-[#2952C4]' : 'text-slate-500 hover:text-slate-700'}`} title="Vista Lista" data-id="element-4012" data-mp="1oy97qplrsvuw">
              
              <ListIcon className="w-4 h-4" data-id="element-4013" data-mp="1f6mp4873rui4" />
            </button>
          </div>
          <Button onClick={() => {
          resetPlanForm();
          setIsPlanModalOpen(true);
        }} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-4015" data-mp="27rd5am23zsus" />} size="sm" data-id="element-4014" data-mp="1a9ml02ttd1tn">
            
            Nueva
          </Button>
        </div>
      </PageHeader>

      {/* Stats Row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 animate-stagger-1" data-id="element-4016" data-mp="2aarl23zggnv3">
        <StatsCard title="Visitas Programadas" value={stats.programadas} icon={<MapPinIcon className="w-5 h-5" data-id="element-4018" data-mp="2k85rdarnzw9o" />} variant="primary" data-id="element-4017" data-mp="2f9uhx15a7nrz" />
        
        <StatsCard title="Cobros Pendientes" value={stats.cobros} icon={<AlertCircleIcon className="w-5 h-5" data-id="element-4020" data-mp="2p80ldn6ql1yy" />} variant="success" data-id="element-4019" data-mp="2k9z2b29b92nh" />
        
        <StatsCard title="Reactivaciones" value={stats.reactivaciones} icon={<ClockIcon className="w-5 h-5" data-id="element-4022" data-mp="2t6q29pqzh1nc" />} variant="warning" data-id="element-4021" data-mp="2t93uzry6y1rq" />
        
        <StatsCard title="Disp. Televentas" value={stats.disponibles} icon={<PhoneIcon className="w-5 h-5" data-id="element-4024" data-mp="32y41rxixq1gh" />} variant="default" data-id="element-4023" data-mp="2y5ud0ktk37fs" />
        
      </div>

      {/* Main Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6 items-start animate-stagger-2" data-id="element-4025" data-mp="37wfb875bi9y6">
        {/* LEFT PANEL */}
        <div className="lg:col-span-3 space-y-4 sm:space-y-6" data-id="element-4026" data-mp="3cye9x3q44ios">
          {isLoading ? <Card data-id="element-4027" data-mp="3gyyayidt46g7">
              <div className="flex items-center justify-center py-16" data-id="element-4028" data-mp="3gyett3vzdm38">
                <div className="w-8 h-8 border-3 border-slate-200 border-t-[#2952C4] rounded-full animate-spin" data-id="element-4029" data-mp="ka65p7tisa2i" />
                <span className="ml-3 text-sm text-slate-500" data-id="element-4030" data-mp="p8hdld098ai3">
                  Cargando planificación...
                </span>
              </div>
            </Card> : viewMode === 'calendar' ? <Card padding="none" className="overflow-hidden" data-id="element-4031" data-mp="y9f9t94gbdq4">
              {/* Calendar header - scrollable on mobile */}
              <div className="overflow-x-auto" data-id="element-4032" data-mp="137gt9v5shf24">
                <div className="grid grid-cols-5 bg-[#0d1b46] text-white min-w-[360px]" data-id="element-4033" data-mp="186a57j5fgy06">
                  {weekDays.map((date, i) => <div key={i} className="py-2 sm:py-3 px-1.5 sm:px-2 text-center border-r border-white/10 last:border-0" data-id="element-4034" data-mp="1cyl6apbwmpub" data-mpi={i}>
                  
                      <p className="text-[10px] sm:text-xs font-medium opacity-70 uppercase" data-id="element-4035" data-mp="1hyprutro6are">
                        {date.toLocaleDateString('es-DO', {
                    weekday: 'short'
                  })}
                      </p>
                      <p className="text-base sm:text-lg font-bold" data-id="element-4036" data-mp="3bfvgfw8dx6vc">
                        {date.getDate()}
                      </p>
                    </div>)}
                </div>
              </div>
              <div className="overflow-x-auto" data-id="element-4037" data-mp="32dvvwty5vknn">
                <div className="grid grid-cols-5 min-h-[280px] sm:min-h-[400px] bg-slate-50 min-w-[360px]" data-id="element-4038" data-mp="32grtgh63k7bk">
                  {weekDays.map((date, i) => {
                const dayEntries = currentWeekEntries.filter(e => {
                  const entryDate = new Date(e.fecha);
                  return entryDate.getDate() === date.getDate() && entryDate.getMonth() === date.getMonth() && entryDate.getFullYear() === date.getFullYear();
                });
                return <div key={i} className="border-r border-slate-200 last:border-0 p-1.5 sm:p-2 space-y-1.5 sm:space-y-2" data-id="element-4039" data-mp="3gddrbs5ypw5z" data-mpi={i}>
                      
                        {dayEntries.map(entry => <div key={entry.id} className={`p-1.5 sm:p-2 rounded-lg shadow-sm text-white text-[10px] sm:text-xs ${getVisitTypeColor(entry.tipoGestion)}`} data-id="element-4040" data-mp="cx6m3h7wfqv3">
                        
                            {entry.hora && <p className="opacity-80 mb-0.5 text-[9px] sm:text-[10px]" data-id="element-4041" data-mp="10tbak3wf5eb4">
                                {entry.hora}
                              </p>}
                            <p className="font-bold truncate leading-tight" data-id="element-4042" data-mp="r38kocklggh0">
                              {entry.clienteNombre}
                            </p>
                            <p className="opacity-90 truncate mt-0.5 hidden sm:block" data-id="element-4043" data-mp="d1w5w4h66kwp">
                              {getVisitTypeLabel(entry.tipoGestion)}
                            </p>
                          </div>)}
                        {dayEntries.length === 0 && <div className="border-2 border-dashed border-emerald-200 bg-emerald-50 rounded-lg p-1.5 sm:p-2 text-center mt-2 sm:mt-4" data-id="element-4044" data-mp="844edbkft0t1">
                            <p className="text-[9px] sm:text-[10px] font-medium text-emerald-600" data-id="element-4045" data-mp="1yhboj0uq04z6">
                              Disponible
                            </p>
                          </div>}
                      </div>;
              })}
                </div>
              </div>
              <div className="p-3 sm:p-4 border-t border-slate-100 flex flex-wrap gap-3 sm:gap-4 bg-white" data-id="element-4046" data-mp="3izvxafxcm061">
                <div className="flex items-center gap-2" data-id="element-4047" data-mp="ln0kt5j2zu9">
                  <div className="w-3 h-3 rounded-full bg-blue-600" data-id="element-4048" data-mp="3rzsr0vylhp7y"></div>
                  <span className="text-xs text-slate-600" data-id="element-4049" data-mp="3rxpstonagps5">
                    Visita de Venta
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-4050" data-mp="5kr71c12ijgw">
                  <div className="w-3 h-3 rounded-full bg-emerald-600" data-id="element-4051" data-mp="5mao34ujsyjq"></div>
                  <span className="text-xs text-slate-600" data-id="element-4052" data-mp="jixz2bjc5p8n">Cobranza</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-4053" data-mp="el5zqwgd30uf">
                  <div className="w-3 h-3 rounded-full bg-amber-600" data-id="element-4054" data-mp="2lakklxkgkrxq"></div>
                  <span className="text-xs text-slate-600" data-id="element-4055" data-mp="2gc0wbqie7427">Reactivación</span>
                </div>
                <div className="flex items-center gap-2" data-id="element-4056" data-mp="2iqo53918r0kr">
                  <div className="w-3 h-3 rounded-full bg-violet-600" data-id="element-4057" data-mp="2iq4lzozuu655"></div>
                  <span className="text-xs text-slate-600" data-id="element-4058" data-mp="2mr83efxjwa8h">Seguimiento</span>
                </div>
              </div>
            </Card> : <Card padding="none" data-id="element-4059" data-mp="2rrvut2poyt38">
              {/* ── MOBILE LIST VIEW (< sm) ── */}
              <div className="sm:hidden divide-y divide-slate-100" data-id="element-4060" data-mp="2wp4e56mkt362">
                {currentWeekEntries.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => <div key={entry.id} className="p-3.5" data-id="element-4061" data-mp="24104ka0vw5y3">
                      <div className="flex items-start justify-between gap-2" data-id="element-4062" data-mp="23zf8gfyx8ey4">
                        <div className="min-w-0 flex-1" data-id="element-4063" data-mp="28zjqiafh9hqg">
                          <p className="font-semibold text-slate-900 text-sm" data-id="element-4064" data-mp="2druqfda7x3j0">
                            {entry.clienteNombre}
                          </p>
                          <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-4065" data-mp="1gactjfvo907b">
                            {entry.codigoCliente} · {entry.zona}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5" data-id="element-4066" data-mp="1dr95ym6r7tsf">
                            {entry.vendedorNombre} ·{' '}
                            {entry.fecha.toLocaleDateString('es-DO', {
                      weekday: 'short',
                      day: 'numeric'
                    })}
                          </p>
                        </div>
                        <div className="flex flex-col items-end gap-1.5 flex-shrink-0" data-id="element-4067" data-mp="utf1mlgkm6gd">
                          <Badge variant="default" className="text-xs" data-id="element-4068" data-mp="q2gg97gq6i69">
                            {getVisitTypeLabel(entry.tipoGestion)}
                          </Badge>
                          <button onClick={() => handleToggleStatus(entry)} disabled={updatingStatusId === entry.id} className="inline-flex items-center transition-transform active:scale-95 disabled:opacity-50" data-id="element-4069" data-mp="l0r3nndutwg2">
                      
                            {updatingStatusId === entry.id ? <div className="w-4 h-4 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-4070" data-mp="73uf5sl7kr2c" /> : <Badge variant={getStatusBadgeVariant(entry.estado)} data-id="element-4071" data-mp="3sm1r2urabncz">
                        
                                {getStatusLabel(entry.estado)}
                              </Badge>}
                          </button>
                        </div>
                      </div>
                    </div>)}
              </div>

              {/* ── DESKTOP TABLE (sm+) ── */}
              <div className="hidden sm:block overflow-x-auto" data-id="element-4072" data-mp="g7hmszedo6lh">
                <table className="w-full text-sm" data-id="element-4073" data-mp="k7jmj30jypy4">
                  <thead className="bg-slate-50 border-b border-slate-200" data-id="element-4074" data-mp="k706xsydkdn9">
                    <tr data-id="element-4075" data-mp="6fbsvk4kff3n">
                      <th className="px-4 py-3 text-left font-semibold text-slate-500 font-heading" data-id="element-4076" data-mp="6g4w4b4bfmh1">
                        Cliente
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500 font-heading" data-id="element-4077" data-mp="z4shs7sz8dv4">
                        RV
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-slate-500 font-heading" data-id="element-4078" data-mp="13xlqf6rkg6ch">
                        Fecha
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-500 font-heading" data-id="element-4079" data-mp="1987mkwpfx8h7">
                        Tipo
                      </th>
                      <th className="px-4 py-3 text-center font-semibold text-slate-500 font-heading" data-id="element-4080" data-mp="1i1kp8qdb3ogh">
                        Estado
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-4081" data-mp="qamwxneuq5vj">
                    {currentWeekEntries.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map(entry => <tr key={entry.id} className="hover:bg-slate-50" data-id="element-4082" data-mp="147jndnr25lbw">
                          <td className="px-4 py-3" data-id="element-4083" data-mp="z9i3j0lt5hzd">
                            <p className="font-medium text-slate-900" data-id="element-4084" data-mp="2fojn34fqj2fy">
                              {entry.clienteNombre}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-4085" data-mp="2as2pljmnbbq7">
                              {entry.codigoCliente} • {entry.zona}
                            </p>
                          </td>
                          <td className="px-4 py-3 text-slate-600" data-id="element-4086" data-mp="d2nuxojjs47o">
                            {entry.vendedorNombre}
                          </td>
                          <td className="px-4 py-3 text-slate-600" data-id="element-4087" data-mp="msqr1xlytya8">
                            {entry.fecha.toLocaleDateString('es-DO', {
                      weekday: 'short',
                      day: 'numeric'
                    })}
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-4088" data-mp="10ryx9agjq6nx">
                            <Badge variant="default" className="text-xs" data-id="element-4089" data-mp="10si6m2s4rmqc">
                              {getVisitTypeLabel(entry.tipoGestion)}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-center" data-id="element-4090" data-mp="32l6dcm3zgsav">
                            <button onClick={() => handleToggleStatus(entry)} disabled={updatingStatusId === entry.id} className="inline-flex items-center transition-transform hover:scale-105 active:scale-95 disabled:opacity-50" title="Clic para cambiar estado" data-id="element-4091" data-mp="37jhi4iff8cm8">
                        
                              {updatingStatusId === entry.id ? <div className="w-4 h-4 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-4092" data-mp="2q9nhxnxhgdot" /> : <Badge variant={getStatusBadgeVariant(entry.estado)} data-id="element-4093" data-mp="2lbltsol79o6h">
                          
                                  {getStatusLabel(entry.estado)}
                                </Badge>}
                            </button>
                          </td>
                        </tr>)}
                  </tbody>
                </table>
              </div>
              <Pagination currentPage={currentPage} totalPages={Math.ceil(currentWeekEntries.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} totalItems={currentWeekEntries.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-4094" data-mp="34wyrjka3tnma" />
            
            </Card>}
        </div>

        {/* RIGHT PANEL */}
        <div className="lg:col-span-2" data-id="element-4095" data-mp="s9ftvqrop7b7">
          <Card padding="none" className="h-full flex flex-col" data-id="element-4096" data-mp="s84stypmidns">
            <div className="flex border-b border-slate-200" data-id="element-4097" data-mp="nc61q3cwuhjo">
              <button onClick={() => setActiveTab('planificacion')} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'planificacion' ? 'border-[#2952C4] text-[#2952C4]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4098" data-mp="11zr0xm6nzwmb">
                
                Planificación
              </button>
              <button onClick={() => setActiveTab('televentas')} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'televentas' ? 'border-[#2952C4] text-[#2952C4]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4099" data-mp="16yc3hs9e2sdx">
                
                Televentas
              </button>
              <button onClick={() => setActiveTab('resumen')} className={`flex-1 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === 'resumen' ? 'border-[#2952C4] text-[#2952C4]' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-id="element-4100" data-mp="3mscfnt2s0h63">
                
                Resumen
              </button>
            </div>

            <div className="flex-1 p-4 overflow-y-auto max-h-[600px]" data-id="element-4101" data-mp="1nk2ypytn1hpg">
              {activeTab === 'planificacion' && <div className="space-y-4" data-id="element-4102" data-mp="1shmfb5dt66kk">
                  <div className="relative" data-id="element-4103" data-mp="1sgb78tdbycnn">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-4104" data-mp="1eid82v7kymsq" />
                    <input type="text" placeholder="Buscar cliente..." value={planSearchQuery} onChange={e => setPlanSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] bg-white" data-id="element-4105" data-mp="1jir57i7n8cbz" />
                  
                  </div>
                  <div className="flex items-center justify-between text-xs text-slate-500 mb-2" data-id="element-4106" data-mp="2ywn7czzpq4lv">
                    <span data-id="element-4107" data-mp="33v6uv4tyfogc">Cliente</span>
                    <span data-id="element-4108" data-mp="32gnojsstudhh">Estado Gestión</span>
                  </div>
                  {(() => {
                const filtered = currentWeekEntries.filter(entry => planSearchQuery ? entry.clienteNombre.toLowerCase().includes(planSearchQuery.toLowerCase()) || entry.vendedorNombre.toLowerCase().includes(planSearchQuery.toLowerCase()) || entry.zona.toLowerCase().includes(planSearchQuery.toLowerCase()) : true);
                const visible = planSearchQuery ? filtered : filtered.slice(0, PLAN_VISIBLE_LIMIT);
                const hiddenCount = filtered.length - visible.length;
                return <>
                        {visible.map(entry => <div key={entry.id} className="flex items-start justify-between p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-4109" data-mp="3bf98nsdy60kd">
                        
                            <div data-id="element-4110" data-mp="7t0nsyxql6zc">
                              <p className="font-semibold text-slate-900 text-sm" data-id="element-4111" data-mp="7u3f37yz1bfs">
                                {entry.clienteNombre}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5" data-id="element-4112" data-mp="1lpbs10d7ndr2">
                                {entry.vendedorNombre} •{' '}
                                {entry.fecha.toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'short'
                        })}
                              </p>
                              <span className="inline-block mt-1.5 px-2 py-0.5 bg-white border border-slate-200 rounded text-[10px] text-slate-600 font-medium" data-id="element-4113" data-mp="24n4jn6idw195">
                                {getVisitTypeLabel(entry.tipoGestion)}
                              </span>
                            </div>
                            <div className="flex flex-col items-end gap-1" data-id="element-4114" data-mp="2ie1bgv4hkef5">
                              <div className="flex items-center gap-1.5" data-id="element-4115" data-mp="2ig3yd6acdrfn">
                                <span className="text-[10px] font-medium text-rose-600" data-id="element-4116" data-mp="2nf75qxdso9qs">
                                  En gestión por RV
                                </span>
                                <div className="w-2 h-2 rounded-full bg-rose-500 animate-pulse" data-id="element-4117" data-mp="2eofc1h6658d0"></div>
                              </div>
                              <button onClick={() => handleToggleStatus(entry)} disabled={updatingStatusId === entry.id} className="inline-flex items-center transition-transform hover:scale-105 active:scale-95 disabled:opacity-50 cursor-pointer" title="Clic para cambiar estado" data-id="element-4118" data-mp="29oszl9y5zw59">
                            
                                {updatingStatusId === entry.id ? <div className="w-4 h-4 border-2 border-slate-300 border-t-[#2952C4] rounded-full animate-spin" data-id="element-4119" data-mp="1qskse0lldb7b" /> : <Badge variant={getStatusBadgeVariant(entry.estado)} size="sm" data-id="element-4120" data-mp="25pbrs9v9mmwx">
                              
                                    {getStatusLabel(entry.estado)}
                                  </Badge>}
                              </button>
                            </div>
                          </div>)}
                        {hiddenCount > 0 && !planSearchQuery && <div className="text-center py-2" data-id="element-4121" data-mp="3jowpzdkper4w">
                            <p className="text-xs text-slate-400" data-id="element-4122" data-mp="3jnc0419c3wdd">
                              +{hiddenCount} más — usa el buscador para
                              encontrarlos
                            </p>
                          </div>}
                        {filtered.length === 0 && <div className="text-center py-6" data-id="element-4123" data-mp="3eqlktg5ct4t0">
                            <SearchIcon className="w-6 h-6 text-slate-300 mx-auto mb-2" data-id="element-4124" data-mp="6a0hofzel5xt" />
                            <p className="text-sm text-slate-500" data-id="element-4125" data-mp="b8tus7as2uxg">
                              {planSearchQuery ? `No se encontraron resultados para "${planSearchQuery}"` : 'No hay planificaciones esta semana'}
                            </p>
                          </div>}
                      </>;
              })()}
                  <div className="pt-4 mt-4 border-t border-slate-100 grid grid-cols-2 gap-3" data-id="element-4126" data-mp="p82zoatja2m4">
                    <div className="bg-[#2952C4] text-white p-3 rounded-lg text-center" data-id="element-4127" data-mp="tz2yjlm7qur9">
                      <p className="text-xs opacity-80" data-id="element-4128" data-mp="13vbjg00wrg9p">Visitas Programadas</p>
                      <p className="text-xl font-bold font-heading" data-id="element-4129" data-mp="13t7bn6zw1x45">
                        {stats.programadas}
                      </p>
                    </div>
                    <div className="bg-emerald-600 text-white p-3 rounded-lg text-center" data-id="element-4130" data-mp="utbxquey7o0f">
                      <p className="text-xs opacity-80" data-id="element-4131" data-mp="puh7j7enix6a">Cobros Pendientes</p>
                      <p className="text-xl font-bold font-heading" data-id="element-4132" data-mp="kwz7k6im3saf">
                        {stats.cobros}
                      </p>
                    </div>
                  </div>
                </div>}

              {activeTab === 'televentas' && <div className="space-y-4" data-id="element-4133" data-mp="c4x5k3swa3y1">
                  <Button className="w-full" size="sm" onClick={() => {
                resetCallForm();
                setIsCallModalOpen(true);
              }} leftIcon={<PhoneIcon className="w-4 h-4" data-id="element-4135" data-mp="25g3rxgrka7c" />} data-id="element-4134" data-mp="7515gfnbiwnr">
                  
                    Registrar Llamada
                  </Button>
                  <div className="space-y-3" data-id="element-4136" data-mp="3j8c87rzgrmno">
                    {televentasEntries.length === 0 && <div className="text-center py-8" data-id="element-4137" data-mp="3jaex28ovr9qt">
                        <PhoneIcon className="w-8 h-8 text-slate-300 mx-auto mb-2" data-id="element-4138" data-mp="3o6dracww3lz5" />
                        <p className="text-sm text-slate-500" data-id="element-4139" data-mp="3t5ymqdcupc74">
                          No hay llamadas registradas
                        </p>
                      </div>}
                    {televentasEntries.map(entry => <div key={entry.id} className="p-3 bg-slate-50 rounded-lg border border-slate-100" data-id="element-4140" data-mp="30im3ac1zlki1">
                    
                        <div className="flex justify-between items-start mb-2" data-id="element-4141" data-mp="35hfh65l6rdiq">
                          <div data-id="element-4142" data-mp="3aez6q1nc3cpz">
                            <p className="font-semibold text-slate-900 text-sm" data-id="element-4143" data-mp="3aepfybugt1j8">
                              {entry.clienteNombre}
                            </p>
                            <p className="text-xs text-slate-500" data-id="element-4144" data-mp="2hqa3nx2pwxbl">
                              {entry.codigoCliente}
                            </p>
                          </div>
                          {getTeleventasResultBadge(entry.resultado)}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-slate-500 border-t border-slate-200 pt-2 mt-2" data-id="element-4145" data-mp="qevui8t2789">
                          <UserIcon className="w-3 h-3" data-id="element-4146" data-mp="eqhc601gssle" />
                          <span data-id="element-4147" data-mp="9quxrn9wh6b0">{entry.agente}</span>
                          <span className="mx-1" data-id="element-4148" data-mp="9p0dmb6gdi7z">•</span>
                          <span data-id="element-4149" data-mp="npulmspl9h64">{entry.fecha.toLocaleDateString()}</span>
                        </div>
                        {entry.notas && <p className="text-xs text-slate-600 mt-2 bg-white p-2 rounded border border-slate-100 italic" data-id="element-4150" data-mp="jnpwa666b6pa">
                            "{entry.notas}"
                          </p>}
                      </div>)}
                  </div>
                </div>}

              {activeTab === 'resumen' && <div className="space-y-6" data-id="element-4151" data-mp="17dk9wpybgzal">
                  <div data-id="element-4152" data-mp="17c946klb9hgu">
                    <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-4153" data-mp="12f8ulzp95wkz">
                      Cumplimiento Semanal
                    </h4>
                    <div className="space-y-3" data-id="element-4154" data-mp="1m1k7cazc1q06">
                      {(() => {
                    const visitados = currentWeekEntries.filter(e => e.estado === 'visitado').length;
                    const pctVisitas = currentWeekEntries.length ? Math.round(visitados / currentWeekEntries.length * 100) : 0;
                    const cobrosHechos = currentWeekEntries.filter(e => e.tipoGestion === 'visita_cobro' && e.estado === 'visitado').length;
                    const cobrosTotal = currentWeekEntries.filter(e => e.tipoGestion === 'visita_cobro').length;
                    const pctCobros = cobrosTotal ? Math.round(cobrosHechos / cobrosTotal * 100) : 0;
                    return <>
                            <div data-id="element-4155" data-mp="ybpwfyykqbik">
                              <div className="flex justify-between text-xs mb-1" data-id="element-4156" data-mp="je77owlwbyb4">
                                <span data-id="element-4157" data-mp="jgjp98nqs6nn">Visitas Realizadas</span>
                                <span className="font-bold" data-id="element-4158" data-mp="obz2bydxdyj0">{pctVisitas}%</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-4159" data-mp="1ffaqzan35hbh">
                                <div className="bg-[#2952C4] h-2 rounded-full transition-all" style={{
                            width: `${pctVisitas}%`
                          }} data-id="element-4160" data-mp="1kcanouy1p7px">
                              </div>
                              </div>
                            </div>
                            <div data-id="element-4161" data-mp="wmzqfnfwlvgj">
                              <div className="flex justify-between text-xs mb-1" data-id="element-4162" data-mp="wq43zbf9e3jv">
                                <span data-id="element-4163" data-mp="11op4lbyfap8u">Cobros Efectivos</span>
                                <span className="font-bold" data-id="element-4164" data-mp="mqdc7hi322nh">{pctCobros}%</span>
                              </div>
                              <div className="w-full bg-slate-100 rounded-full h-2" data-id="element-4165" data-mp="rpzkdcj20spf">
                                <div className="bg-emerald-600 h-2 rounded-full transition-all" style={{
                            width: `${pctCobros}%`
                          }} data-id="element-4166" data-mp="2h3mlp9ampo5q">
                              </div>
                              </div>
                            </div>
                          </>;
                  })()}
                    </div>
                  </div>
                  <div data-id="element-4167" data-mp="k55kq5v9nx8b">
                    <h4 className="text-sm font-bold text-slate-900 mb-3 font-heading" data-id="element-4168" data-mp="k6gv4oj7w38e">
                      Desglose por Tipo
                    </h4>
                    <div className="space-y-2" data-id="element-4169" data-mp="fcvyaztd1fas">
                      {(['visita_venta', 'visita_cobro', 'reactivacion', 'seguimiento'] as VisitType[]).map(type => {
                    const count = currentWeekEntries.filter(e => e.tipoGestion === type).length;
                    const pct = currentWeekEntries.length ? Math.round(count / currentWeekEntries.length * 100) : 0;
                    return <div key={type} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded" data-id="element-4170" data-mp="y33roq79crds">
                          
                            <span className="font-medium text-slate-700" data-id="element-4171" data-mp="2zxcy1dka8631">
                              {getVisitTypeLabel(type)}
                            </span>
                            <div className="flex items-center gap-2" data-id="element-4172" data-mp="18si269mt6sf5">
                              <span className="text-slate-500" data-id="element-4173" data-mp="18srrdv014vjs">{count}</span>
                              <span className="font-bold w-8 text-right" data-id="element-4174" data-mp="1ctbq33094781">
                                {pct}%
                              </span>
                            </div>
                          </div>;
                  })}
                    </div>
                  </div>
                </div>}
            </div>
          </Card>
        </div>
      </div>

      {/* MODAL: Nueva Planificación */}
      <Modal isOpen={isPlanModalOpen} onClose={() => setIsPlanModalOpen(false)} title="Nueva Planificación" data-id="element-4175" data-mp="1wjdpatgbkzg2">
        
        <ModalBody data-id="element-4176" data-mp="20iw91lwen4hb">
          <div className="space-y-4" data-id="element-4177" data-mp="20lif1h3ndbxf">
            {saveError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-4178" data-mp="25jttk54w0alz">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-4179" data-mp="2ai4zi4s2a0yx" />
                {saveError}
              </div>}
            <div className="grid grid-cols-2 gap-4" data-id="element-4180" data-mp="2fmp9suh73il2">
              <div data-id="element-4181" data-mp="2flxqoj4sbdbo">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4182" data-mp="2fle6swvkqev0">
                  Fecha
                </label>
                <input type="date" className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={planFormDate} onChange={e => setPlanFormDate(e.target.value)} data-id="element-4183" data-mp="26m0xq5bd3sat" />
                
              </div>
              <div data-id="element-4184" data-mp="2ydqiia84asht">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4185" data-mp="2yf9x7we4kvhh">
                  Tipo Gestión
                </label>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={planFormTipo} onChange={e => setPlanFormTipo(e.target.value as VisitType)} data-id="element-4186" data-mp="2pleuev0h7h50">
                  
                  <option value="visita_venta" data-id="element-4187" data-mp="3harpg0l8f2oe">Visita de Venta</option>
                  <option value="visita_cobro" data-id="element-4188" data-mp="31t04tu44jdy7">Visita de Cobro</option>
                  <option value="reactivacion" data-id="element-4189" data-mp="36suy24bdgfmf">Reactivación</option>
                  <option value="seguimiento" data-id="element-4190" data-mp="36r1p2idadjb9">Seguimiento</option>
                </select>
              </div>
            </div>
            <div data-id="element-4191" data-mp="2wuovrllnl7h1">
              <SearchableSelect label="Cliente" value={planFormCliente} onChange={val => {
              const client = clients.find(c => c.nombre === val);
              setPlanFormCliente(val);
              setPlanFormCodigo(client?.codigo || client?.id || '');
              setPlanFormZona(client?.zona || 'General');
            }} options={clients.map(c => c.nombre)} placeholder="Buscar cliente..." maxVisible={5} data-id="element-4192" data-mp="2wu6qjzh0jiwz" />
              
            </div>
            <div className="grid grid-cols-2 gap-4" data-id="element-4193" data-mp="3frq5fjq5jvrb">
              <Input label="Código" value={planFormCodigo} readOnly className="bg-slate-50" data-id="element-4194" data-mp="7cy9bzyq8j4n" />
              
              <Input label="Zona" value={planFormZona} readOnly className="bg-slate-50" data-id="element-4195" data-mp="c5j1yy5wcq6l" />
              
            </div>
            {isAdmin && <div data-id="element-4196" data-mp="1wt1zuox9h987">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4197" data-mp="1rwbhtw1wc3k5">
                  Vendedor
                </label>
                <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={planFormVendedorId} onChange={e => {
              const v = users.find(u => u.id === e.target.value);
              setPlanFormVendedorId(e.target.value);
              setPlanFormVendedorNombre(v?.nombre || '');
            }} data-id="element-4198" data-mp="26jwiljbatqow">
                
                  <option value="" data-id="element-4199" data-mp="1nvhtx2ogeor9">Seleccionar vendedor...</option>
                  {users.filter(u => u.activo).map(u => <option key={u.id} value={u.id} data-id="element-4200" data-mp="1iubxatroahcg">
                        {u.nombre}
                      </option>)}
                </select>
              </div>}
          </div>
        </ModalBody>
        <ModalFooter data-id="element-4201" data-mp="2u9h4ob2r142e">
          <Button variant="secondary" onClick={() => setIsPlanModalOpen(false)} data-id="element-4202" data-mp="2ua0knm8u9ids">
            Cancelar
          </Button>
          <Button onClick={handleSavePlan} isLoading={isSaving} disabled={!planFormCliente || !planFormDate} data-id="element-4203" data-mp="31ftfop1qx4b8">
            
            Guardar Planificación
          </Button>
        </ModalFooter>
      </Modal>

      {/* MODAL: Registrar Llamada */}
      <Modal isOpen={isCallModalOpen} onClose={() => setIsCallModalOpen(false)} title="Registrar Gestión Televentas" data-id="element-4204" data-mp="3gb98kqqlau61">
        
        <ModalBody data-id="element-4205" data-mp="2iosctpxi8ome">
          <div className="space-y-4" data-id="element-4206" data-mp="2ilecen8eiphl">
            {saveError && <div className="flex items-start gap-3 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-4207" data-mp="2nndfrx455mej">
                <AlertCircleIcon className="w-4 h-4 flex-shrink-0 mt-0.5" data-id="element-4208" data-mp="2sk3vgjc1afzf" />
                {saveError}
              </div>}
            <div data-id="element-4209" data-mp="2xdyerlnq8sxz">
              <SearchableSelect label="Cliente" value={callFormCliente} onChange={val => {
              const client = clients.find(c => c.nombre === val);
              setCallFormCliente(val);
              setCallFormCodigo(client?.id || '');
            }} options={clients.map(c => c.nombre)} placeholder="Buscar cliente..." maxVisible={5} data-id="element-4210" data-mp="1zwzklxq9f7fq" />
              
            </div>
            <div data-id="element-4211" data-mp="hxa4nhtvr6gd">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4212" data-mp="czrrff8ty52k">
                Resultado
              </label>
              <select className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={callFormResultado} onChange={e => setCallFormResultado(e.target.value as TeleventasResult)} data-id="element-4213" data-mp="7zvnfg04udn0">
                
                <option value="no_contesto" data-id="element-4214" data-mp="3qexu0cm7kjl5">No Contestó</option>
                <option value="promesa_pago" data-id="element-4215" data-mp="3qffvporn3q0g">Promesa de Pago</option>
                <option value="venta_realizada" data-id="element-4216" data-mp="3lgmlc57ngf4i">Venta Realizada</option>
                <option value="seguimiento" data-id="element-4217" data-mp="3gggzj5o29muc">Seguimiento</option>
              </select>
            </div>
            <div data-id="element-4218" data-mp="3bhxdl59gwb1z">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-4219" data-mp="3bhduhl82zgmd">
                Notas
              </label>
              <textarea className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none h-24 resize-none" placeholder="Detalles de la llamada..." value={callFormNotas} onChange={e => setCallFormNotas(e.target.value)} data-id="element-4220" data-mp="147rwbr78w827" />
              
            </div>
          </div>
        </ModalBody>
        <ModalFooter data-id="element-4221" data-mp="z96ytzjdo2hg">
          <Button variant="secondary" onClick={() => setIsCallModalOpen(false)} data-id="element-4222" data-mp="khy1135bnat8">
            Cancelar
          </Button>
          <Button onClick={handleSaveCall} isLoading={isSaving} disabled={!callFormCliente} data-id="element-4223" data-mp="pa7yd48rnd5j">
            
            Registrar Llamada
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}